import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { groupBy } from 'lodash';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  hideViewSidePanel,
  showViewSidePanel,
} from 'views/mainApp/viewSidePanel/actions';
import { ViewSidePanelContentIds } from 'views/mainApp/viewSidePanel/types';
import { perspectivesEditor$ } from 'perspectiveSidebar/perspectiveEditor/perspectivesEditor$';
import { viewSidePanel$ } from 'views/mainApp/viewSidePanel/viewSidePanel$';
import {
  trackMultiLabelFormattingApply,
  trackConditionalFormattingApply,
  trackConditionalFormattingClear,
  trackMultiLabelFormattingClear,
  trackViewOptionsClose,
} from '../../tracking';
import { getLabelFormatingRulesWithFieldsCountByType } from 'utils/getLabelFormatingRulesWithFieldsCountByType';
import { featureContext$ } from 'streams/featureContext$';
import { featureOperations, Features } from '@ardoq/features';
import {
  closeSidebarPanel,
  openSidebarPanel,
} from 'canvas/sidebarPanel/actions';
import { SidebarPanelContentIds } from 'canvas/sidebarPanel/types';
import { sidebarPanel$ } from 'canvas/sidebarPanel/sidebarPanel$';
import { APIEntityType } from '@ardoq/api-types';
import {
  applyGroupingRules,
  applyLabelFormattingRules,
  applyConditionalFormattingRules,
  clearGroupingRules,
  clearLabelFormattingRules,
  clearConditionalFormattingRules,
} from 'perspectiveSidebar/perspectiveEditor/perspectivesEditor$/actions';
import { i18nLocale$ } from 'streams/i18nLocale$';

export const viewOptionsPerspectivesPage$ = combineLatest([
  perspectivesEditor$,
  viewSidePanel$,
  featureContext$,
  sidebarPanel$,
  i18nLocale$,
]).pipe(
  map(
    ([
      perspectiveEditorState,
      viewSidePanel,
      featureContext,
      sidebarPanel,
      i18nLocale,
    ]) => {
      // KFQ12026
      const isCanvasPrototype = featureOperations.hasFeature(
        featureContext,
        Features.CANVAS_PROTOTYPE
      );
      const isSidebarPanelOpened = sidebarPanel.contentId !== null;

      // In canvas prototype, view options can open from sidebar OR from modal (e.g. "Save as Viewpoint")
      // Use sidebarPanel.contentId only when sidebar is actually open, otherwise fall back to viewSidePanel
      const activeContentId =
        isCanvasPrototype && isSidebarPanelOpened
          ? sidebarPanel.contentId
          : viewSidePanel.contentId;

      const onApply = () => {
        switch (activeContentId) {
          case ViewSidePanelContentIds.PERSPECTIVES_GROUPS:
          case SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_GROUPS: {
            dispatchAction(applyGroupingRules());
            break;
          }

          case ViewSidePanelContentIds.PERSPECTIVES_LABELS:
          case SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_LABELS: {
            dispatchAction(applyLabelFormattingRules());
            const { referenceLabelsCount, componentLabelsCount } =
              getLabelFormatingRulesWithFieldsCountByType(
                perspectiveEditorState.formattingTab.labelFormatting
              );
            trackMultiLabelFormattingApply({
              componentLabelsCount,
              referenceLabelsCount,
              viewStyle: perspectiveEditorState.activeViewId,
            });
            break;
          }

          case ViewSidePanelContentIds.PERSPECTIVES_CONDITIONAL_FORMAT:
          case SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT: {
            dispatchAction(applyConditionalFormattingRules());
            const { keepOriginalColor, conditionalFormattingRules } =
              perspectiveEditorState.formattingTab;
            const rulesByType = groupBy(conditionalFormattingRules, 'type');
            trackConditionalFormattingApply({
              componentRulesCount:
                rulesByType[APIEntityType.COMPONENT]?.length ?? 0,
              referenceRulesCount:
                rulesByType[APIEntityType.REFERENCE]?.length ?? 0,
              tagRulesCount: rulesByType[APIEntityType.TAG]?.length ?? 0,
              keepOriginalColor,
              activeViewId: perspectiveEditorState.activeViewId,
            });
            break;
          }
        }
      };

      const onClose = () => {
        trackViewOptionsClose(perspectiveEditorState.activeViewId);

        if (isCanvasPrototype) {
          if (!isSidebarPanelOpened) {
            // If a sidebar panel is not opened, we assume the currently
            // visible view options are opened as part of "Save as Viewpoint".
            // So, when you close, you want to see "Save as Viewpoint" again.
            dispatchAction(
              showViewSidePanel(ViewSidePanelContentIds.SAVE_VIEWPOINT)
            );
          } else {
            dispatchAction(closeSidebarPanel());
          }
        } else {
          dispatchAction(hideViewSidePanel());
        }
      };

      const onClear = () => {
        switch (activeContentId) {
          case ViewSidePanelContentIds.PERSPECTIVES_GROUPS:
          case SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_GROUPS: {
            dispatchAction(clearGroupingRules());
            break;
          }

          case ViewSidePanelContentIds.PERSPECTIVES_LABELS:
          case SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_LABELS: {
            dispatchAction(clearLabelFormattingRules());
            trackMultiLabelFormattingClear(perspectiveEditorState.activeViewId);
            break;
          }

          case ViewSidePanelContentIds.PERSPECTIVES_CONDITIONAL_FORMAT:
          case SidebarPanelContentIds.VIEW_SETTINGS_PERSPECTIVE_CONDITIONAL_FORMAT: {
            dispatchAction(clearConditionalFormattingRules());
            trackConditionalFormattingClear(
              perspectiveEditorState.activeViewId
            );
            break;
          }
        }
      };

      const onBackButtonClick = () => {
        if (isCanvasPrototype) {
          if (!isSidebarPanelOpened) {
            // If a sidebar panel is not opened, we assume the currently
            // visible view options are opened as part of "Save as Viewpoint".
            // So, when you go back, you want to see "Save as Viewpoint" again.
            dispatchAction(
              showViewSidePanel(ViewSidePanelContentIds.SAVE_VIEWPOINT)
            );
          } else {
            dispatchAction(
              openSidebarPanel(SidebarPanelContentIds.VIEW_SETTINGS)
            );
          }
        } else {
          dispatchAction(
            showViewSidePanel(ViewSidePanelContentIds.VIEW_OPTIONS)
          );
        }
      };

      return {
        onApply,
        onClose,
        onClear,
        onBackButtonClick,
        i18nLocale,
      };
    }
  )
);
